// pages/InviteCodeManager.js
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import UserService from '../services/UserService'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../firebase'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import toast from 'react-hot-toast'
import './InviteCodeManager.css'

function InviteCodeManager () {
  const { user } = useAuth()
  const [inviteCodes, setInviteCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [creating, setCreating] = useState(false)

  // Form data for creating new invite code
  const [formData, setFormData] = useState({
    description: '',
    maxUses: '',
    expiresInDays: '',
    role: 'user'
  })

  const db = getFirestore(app)
  const functions = getFunctions(app)

  useEffect(() => {
    if (!user) {
      setLoading(false)
      setError('Bitte melden Sie sich an')
      return
    }

    let unsubscribe = () => {}

    const setupListener = async () => {
      try {
        // Get user's companyId
        const companyId = await UserService.getUserCompanyId(user.uid)

        // Real-time listener for invite codes
        const inviteCodesRef = collection(
          db,
          'companies',
          companyId,
          'inviteCodes'
        )
        const q = query(inviteCodesRef, orderBy('createdAt', 'desc'))

        unsubscribe = onSnapshot(
          q,
          snapshot => {
            const codes = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            setInviteCodes(codes)
            setLoading(false)
          },
          err => {
            console.error('Error loading invite codes:', err)
            setError('Fehler beim Laden der Einladungscodes')
            setLoading(false)
          }
        )
      } catch (err) {
        console.error('Error setting up listener:', err)
        setError('Fehler beim Initialisieren')
        setLoading(false)
      }
    }

    setupListener()

    return () => unsubscribe()
  }, [db, user])

  const handleCreateCode = async e => {
    e.preventDefault()
    setCreating(true)
    setError(null)

    try {
      const createInviteCode = httpsCallable(functions, 'createInviteCode')
      const params = {
        description: formData.description || undefined,
        role: formData.role || 'user'
      }

      // Add optional parameters
      if (formData.maxUses) {
        params.maxUses = parseInt(formData.maxUses)
      }
      if (formData.expiresInDays) {
        params.expiresInDays = parseInt(formData.expiresInDays)
      }

      const result = await createInviteCode(params)

      if (result.data.success) {
        // Reset form and close
        setFormData({
          description: '',
          maxUses: '',
          expiresInDays: '',
          role: 'user'
        })
        setShowCreateForm(false)
      } else {
        setError(result.data.message || 'Fehler beim Erstellen des Codes')
      }
    } catch (error) {
      console.error('Error creating invite code:', error)
      setError('Fehler beim Erstellen des Einladungscodes')
    } finally {
      setCreating(false)
    }
  }

  const handleToggleActive = async (codeId, currentStatus) => {
    try {
      const companyId = await UserService.getUserCompanyId(user.uid)
      const codeRef = doc(db, 'companies', companyId, 'inviteCodes', codeId)
      await updateDoc(codeRef, {
        isActive: !currentStatus
      })
    } catch (error) {
      console.error('Error toggling code status:', error)
      setError('Fehler beim Aktualisieren des Code-Status')
    }
  }

  const handleDeleteCode = async codeId => {
    if (
      !window.confirm(
        'Sind Sie sicher, dass Sie diesen Einladungscode löschen möchten?'
      )
    ) {
      return
    }

    try {
      const companyId = await UserService.getUserCompanyId(user.uid)
      const codeRef = doc(db, 'companies', companyId, 'inviteCodes', codeId)
      await deleteDoc(codeRef)
      setError(null)
    } catch (error) {
      console.error('Error deleting invite code:', error)
      setError('Fehler beim Löschen des Einladungscodes')
    }
  }

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text)
    toast.success('Text in die Zwischenablage kopiert!')
  }

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className='invite-code-manager'>
        <div className='loading'>Lädt Einladungscodes...</div>
      </div>
    )
  }

  return (
    <div className='invite-code-manager'>
      <div className='page-header'>
        <h1>Einladungscodes verwalten</h1>
        <button
          className='btn-primary'
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Abbrechen' : '+ Neuer Code'}
        </button>
      </div>

      {error && (
        <div className='error-message'>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {showCreateForm && (
        <div className='create-form-card'>
          <h2>Neuen Einladungscode erstellen</h2>
          <form onSubmit={handleCreateCode} className='create-form'>
            <div className='form-group'>
              <label htmlFor='description'>Beschreibung (optional)</label>
              <input
                type='text'
                id='description'
                className='invite-form-input'
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder='z.B. "Neue Techniker 2024"'
                disabled={creating}
              />
            </div>

            <div className='form-row'>
              <div className='form-group'>
                <label htmlFor='maxUses'>Max. Verwendungen (optional)</label>
                <input
                  type='number'
                  id='maxUses'
                  className='invite-form-input'
                  value={formData.maxUses}
                  onChange={e =>
                    setFormData({ ...formData, maxUses: e.target.value })
                  }
                  placeholder='Unbegrenzt'
                  min='1'
                  disabled={creating}
                />
              </div>

              <div className='form-group'>
                <label htmlFor='expiresInDays'>Läuft ab in (Tage)</label>
                <input
                  type='number'
                  id='expiresInDays'
                  className='invite-form-input'
                  value={formData.expiresInDays}
                  onChange={e =>
                    setFormData({ ...formData, expiresInDays: e.target.value })
                  }
                  placeholder='Kein Ablauf'
                  min='1'
                  disabled={creating}
                />
              </div>

              <div className='form-group'>
                <label htmlFor='role'>Rolle</label>
                <select
                  id='role'
                  className='invite-form-select'
                  value={formData.role}
                  onChange={e =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  disabled={creating}
                >
                  <option value='user'>Benutzer</option>
                  <option value='moderator'>Moderator</option>
                  <option value='admin'>Administrator</option>
                </select>
              </div>
            </div>

            <button type='submit' className='btn-primary' disabled={creating}>
              {creating ? 'Erstelle...' : 'Code erstellen'}
            </button>
          </form>
        </div>
      )}

      <div className='codes-list'>
        {inviteCodes.length === 0 ? (
          <div className='empty-state'>
            <p>Noch keine Einladungscodes erstellt.</p>
            <p>Erstellen Sie einen Code, um neue Benutzer einzuladen.</p>
          </div>
        ) : (
          <div className='codes-grid'>
            {inviteCodes.map(code => (
              <div
                key={code.id}
                className={`code-card ${!code.isActive ? 'inactive' : ''}`}
              >
                <div className='code-header'>
                  <div className='code-value'>
                    <code>{code.code}</code>
                    <button
                      className='btn-icon'
                      onClick={() => copyToClipboard(code.code)}
                      title='Code kopieren'
                    >
                      <ContentCopyIcon fontSize='small' />
                    </button>
                  </div>
                  <div className='code-status'>
                    <span
                      className={`status-badge ${
                        code.isActive ? 'active' : 'inactive'
                      }`}
                    >
                      {code.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                </div>

                {code.description && (
                  <div className='code-description'>{code.description}</div>
                )}

                <div className='code-details'>
                  <div className='detail-row'>
                    <span className='label'>Erstellt:</span>
                    <span className='value'>{formatDate(code.createdAt)}</span>
                  </div>

                  <div className='detail-row'>
                    <span className='label'>Erstellt von:</span>
                    <span className='value'>{code.createdBy}</span>
                  </div>

                  {code.expiresAt && (
                    <div className='detail-row'>
                      <span className='label'>Läuft ab:</span>
                      <span
                        className={`value ${
                          new Date(code.expiresAt.toDate()) < new Date()
                            ? 'expired'
                            : ''
                        }`}
                      >
                        {formatDate(code.expiresAt)}
                      </span>
                    </div>
                  )}

                  <div className='detail-row'>
                    <span className='label'>Verwendungen:</span>
                    <span className='value'>
                      {code.usedCount || 0}
                      {code.maxUses ? ` / ${code.maxUses}` : ' / ∞'}
                    </span>
                  </div>

                  {code.role && (
                    <div className='detail-row'>
                      <span className='label'>Rolle:</span>
                      <span className='value role-badge'>{code.role}</span>
                    </div>
                  )}
                </div>

                {code.usedBy && code.usedBy.length > 0 && (
                  <div className='code-users'>
                    <div className='users-header'>
                      Verwendet von ({code.usedBy.length}):
                    </div>
                    <div className='users-list'>
                      {code.usedBy.slice(0, 3).map((usage, idx) => (
                        <div key={idx} className='user-item'>
                          <span className='user-email'>{usage.email}</span>
                          <span className='user-date'>
                            {formatDate(usage.usedAt)}
                          </span>
                        </div>
                      ))}
                      {code.usedBy.length > 3 && (
                        <div className='more-users'>
                          +{code.usedBy.length - 3} weitere
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className='code-actions'>
                  <button
                    className={`btn-secondary ${
                      !code.isActive ? 'btn-activate' : ''
                    }`}
                    onClick={() => handleToggleActive(code.id, code.isActive)}
                  >
                    {code.isActive ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                  <button
                    className='btn-delete'
                    onClick={() => handleDeleteCode(code.id)}
                  >
                    Löschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default InviteCodeManager
